<script setup lang="ts">
import {IonButton, IonCol, IonGrid, IonRow, IonTextarea} from '@ionic/vue';
import Modal from '@/components/Modal.vue'
import InputTime from '@/components/InputTime.vue'
import {computed, PropType, Ref, ref, watch} from 'vue'
import time from "@/helper/datetime/time"
import TextWithIcon from '@/components/TextWithIcon.vue'
import datetime, {convertDateTime2Time} from '@/helper/datetime/datetime'
// import type { AvailabilityTimeConstraint } from '@/graphql/generated/graphql';
import {kapitelTimeString} from '@/graphql/kapitelTypes';
import InlineSVG from '@/components/InlineSVG.vue';
import {Availability, AvailabilityTimeConstraint} from "@/graphql/generated/graphql";
import {availabilityShiftsAndTimeConstraintsToIntervals} from "@/helper/availability";

type TimeConstraintInterface = {
    begin: kapitelTimeString,
    until: kapitelTimeString,
    comment: string
}

const props = defineProps({
    containingAvailability: {
        type: Object as PropType<Availability>,
        required: true,
    },
})

/**
 * Modal v-model pass through
 */
const isOpen = defineModel({type: Boolean, default: false})
const emit = defineEmits<{
    (e: "add:timeConstraint", value: AvailabilityTimeConstraint): void
}>()




/**
 * default values
 */

const availabilityHullTimes = computed(() => {
    if (!props.containingAvailability) {
        return undefined
    }
    const intervals = availabilityShiftsAndTimeConstraintsToIntervals(props.containingAvailability.shifts, [])
    if (intervals.length === 0) {
        return undefined
    }
    return {
        begin: intervals[0].begin,
        until: intervals[intervals.length-1].until
    }
})


/**
 * regular data
 */
const valueBegin : Ref<kapitelTimeString|undefined> = ref(undefined)
const valueUntil : Ref<kapitelTimeString|undefined> = ref(undefined)
const valueComment : Ref<string|undefined>  = ref(undefined)

const validationBothValuesSet = () => {
  if(!valueBegin.value || !valueUntil.value){
    return false
  }
  return true
}

const validSettings = () => {
  if(validationBothValuesSet()){
    return valueUntil.value && valueBegin.value && time.isAfter(valueUntil.value, valueBegin.value)
  }
}

const validationErrorMessage = () => {
    if (validSettings()) {
        return ''
    }
    return validationBothValuesSet()
        ? "Das Ende muss nach dem Begin liegen"
        : "Bitte Start- und Endzeit angeben."
}

const updateValues = () => {
    const defaultDateTimes = availabilityHullTimes.value
    const defaultTimes = {
        begin: defaultDateTimes?.begin ? convertDateTime2Time(defaultDateTimes?.begin) : undefined,
        until: defaultDateTimes?.until ? convertDateTime2Time(defaultDateTimes?.until) : undefined,
    }

    valueBegin.value =  defaultTimes?.begin
    valueUntil.value =  defaultTimes?.until
}

// watch(() => props.timeConstraint, updateValues, {immediate: true})
watch(() => availabilityHullTimes, updateValues, {immediate: true})
watch(() => props.containingAvailability, updateValues, {immediate: true})

const save = () => {
    if (!valueBegin.value || !valueUntil.value){
        return
    }

    const value = {
        begin: datetime.convertDateAndTime2DateTime(props.containingAvailability.date, valueBegin.value),
        until: datetime.convertDateAndTime2DateTime(props.containingAvailability.date, valueUntil.value),
        comment: valueComment.value
    } as AvailabilityTimeConstraint
    if (datetime.isAfter(value.begin, value.until)) {
        value.until = datetime.addDays(value.until, 1);
    }

    emit('add:timeConstraint', value)
    isOpen.value = false
}

</script>

<template>
    <Modal
        v-model="isOpen"
        :title="'Einschränkung hinzufügen'"
        @view:will-present="updateValues"
    >
        <ion-grid>
            <ion-row>
                <ion-col>
                    <p class="text-bold">
                        Ich kann nicht zwischen...
                    </p>
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col>
                    <InputTime
                        label="Anfang"
                        v-model="valueBegin"
                        :nullable="false"
                        :initial-fallback-value="valueUntil ?? '06:30'"
                    />
                </ion-col>
                <ion-col>
                    <InputTime
                        label="Ende"
                        v-model="valueUntil"
                        :nullable="false"
                        :initial-fallback-value="valueBegin ?? '15:30'"
                    />
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col>
                    <ion-textarea
                        label="Grund (optional)"
                        v-model="valueComment"
                        label-placement="stacked"
                        :auto-grow="true"
                    />
                </ion-col>
            </ion-row>
            <ion-row style="height: 4em /*prevent jumping between button & error message*/">
                <ion-col v-if="validSettings()">
                    <ion-button
                        @click="save"
                        expand="block"
                        :disabled="!validSettings()"
                        color="secondary"
                    >
                        Hinzufügen
                    </ion-button>
                </ion-col>
                <ion-col v-if="!validSettings()">
                    <TextWithIcon
                        v-if="!validSettings()"
                        :small="true"
                        :warning="!validSettings()"
                        :success="validSettings()"
                    >
                        <template
                            #icons
                        >
                            <InlineSVG
                                v-if="!validSettings()"
                                src="/icons/info.svg"
                            />
                            <InlineSVG
                                v-else
                                src="/icons/check-circle.svg"
                            />
                        </template>
                        <p>
                            {{ validationErrorMessage() }}
                        </p>
                    </TextWithIcon>
                </ion-col>
            </ion-row>
        </ion-grid>
    </Modal>
</template>
